import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/site/SiteNav";
import { Footer } from "@/components/site/Sections";
import {
  Database,
  CheckCircle2,
  ArrowRight,
  ChevronRight,
  Sparkles,
  BarChart3,
  TrendingUp,
  Target,
  Users,
  Award,
  Globe,
  Clock,
  ShieldCheck,
  Check,
  Building2,
  FileCheck,
  Search,
  Zap,
  Cpu,
  Layers,
  PieChart,
  LineChart,
  Lock,
  Activity,
  Server,
} from "lucide-react";

export const Route = createFileRoute("/services_/data-intelligence")({
  head: () => ({
    meta: [
      {
        title:
          "Data Intelligence Services — KMWAdvertising · BI, GA4 & Predictive Modeling",
      },
      {
        name: "description",
        content:
          "Data Intelligence Services That Turn Business Data into Smarter Marketing and Growth Strategies. Power BI, Looker Studio, BigQuery & Customer Analytics by KMWA LLP.",
      },
      {
        property: "og:title",
        content: "Data Intelligence Services — KMWAdvertising",
      },
      {
        property: "og:description",
        content:
          "Unlock Actionable Insights Through Advanced Data Intelligence. Single source of truth analytics, customer lifetime value (CLV), and predictive forecasting.",
      },
    ],
  }),
  component: DataIntelligenceServicePage,
});

export function DataIntelligenceServicePage() {
  const whyDataIntelligenceMatters = [
    {
      title: "Smarter Executive Decisions",
      desc: "Access real-time, consolidated reporting that eliminates guesswork in strategic business investments.",
      icon: LineChart,
    },
    {
      title: "360-Degree Customer Insight",
      desc: "Analyze buyer journeys across web, mobile, social, and offline touchpoints to increase customer lifetime value (CLV).",
      icon: Users,
    },
    {
      title: "Optimized Marketing Performance",
      desc: "Identify exact campaigns, channels, and audience segments delivering the highest commercial return on investment.",
      icon: Target,
    },
    {
      title: "Predictive Future Forecasting",
      desc: "Anticipate market trends, sales demand, customer churn, and seasonal surges using advanced statistical modeling.",
      icon: Cpu,
    },
    {
      title: "Data Quality & Governance",
      desc: "Build a single, secure source of truth with strict data validation, accuracy checks, and privacy compliance.",
      icon: ShieldCheck,
    },
  ];

  const dataIntelligenceServices = [
    {
      title: "Data Collection & Integration",
      desc: "Unifying data streams from websites, GA4, ad platforms, CRM, ERP, email tools, and mobile apps into one clean warehouse.",
      icon: Server,
    },
    {
      title: "Business Intelligence & Dashboards",
      desc: "Interactive executive dashboards in Looker Studio, Power BI, and Tableau showing real-time KPIs, sales, and revenue metrics.",
      icon: PieChart,
    },
    {
      title: "Customer Intelligence & Segmentation",
      desc: "Customer cohort profiling, behavioral analysis, demographic insights, customer journey mapping, and churn prevention.",
      icon: Users,
    },
    {
      title: "Predictive Analytics & Forecasting",
      desc: "Sales forecasting, demand prediction, revenue modeling, customer churn risk modeling, and seasonal trend forecasting.",
      icon: TrendingUp,
    },
    {
      title: "Marketing Intelligence & Attribution",
      desc: "Cross-channel campaign attribution, lead quality scoring, media performance reporting, and ad budget optimization.",
      icon: BarChart3,
    },
    {
      title: "Data Governance & Compliance",
      desc: "Data accuracy validation, documentation standards, privacy regulation compliance (GDPR, DPDP), and secure ETL pipelines.",
      icon: Lock,
    },
  ];

  const techPlatformsList = [
    "Google Analytics 4 (GA4)",
    "Google Tag Manager (GTM)",
    "Google Looker Studio",
    "Microsoft Power BI",
    "Tableau Desktop & Server",
    "Google BigQuery & Cloud SQL",
    "Salesforce & HubSpot CRM",
    "Zoho CRM & Analytics",
    "AI-Powered Predictive Models",
  ];

  const predictiveModelingCapabilities = [
    "Sales & Revenue Forecasting",
    "Product Demand & Inventory Planning",
    "Customer Churn Risk Prediction",
    "Marketing ROI & Media Spend Projections",
    "Customer Lifetime Value (CLV) Modeling",
    "Seasonal Sales Trend Analysis",
    "Lead Score & Quality Probability",
    "Budget Reallocation Optimization",
  ];

  const kmwaStrengths = [
    "25+ years of strategic media, advertising & digital transformation experience",
    "Certified Data Consultants & Business Intelligence specialists",
    "Data warehousing & ETL integration expertise (BigQuery, SQL)",
    "Custom interactive dashboard development (Power BI, Looker Studio, Tableau)",
    "Cross-channel marketing attribution modeling",
    "Customer cohort profiling & CLV analytics",
    "AI-assisted predictive interpretative models",
    "Full privacy & data governance compliance (GDPR, DPDP)",
    "Single source of truth architecture design",
    "ROI-focused decision-making recommendations",
  ];

  return (
    <div className="dark relative min-h-screen bg-background text-foreground flex flex-col w-full overflow-x-clip">
      <SiteNav />

      <main className="flex-1 w-full overflow-x-clip pt-24 md:pt-28">
        {/* HERO SECTION */}
        <section className="relative overflow-hidden py-14 sm:py-20 md:py-24 border-b border-white/10">
          <div className="absolute inset-0 grid-bg opacity-15" aria-hidden />
          <div className="glow-orb -left-32 top-10 h-[450px] w-[450px] bg-[var(--glow-purple)] opacity-25" />
          <div className="glow-orb right-[-100px] top-20 h-[400px] w-[400px] bg-[var(--glow-cyan)] opacity-25" />

          <div className="container-x relative z-10">
            {/* Breadcrumb */}
            <div className="flex flex-wrap items-center gap-2.5 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
              <Link to="/" className="hover:text-white transition">
                Home
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <Link to="/services" className="hover:text-white transition">
                Services
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <span className="text-[var(--glow-purple)] font-semibold">Data Intelligence</span>
            </div>

            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-muted-foreground border border-white/10 mb-6">
                <Database className="h-3.5 w-3.5 text-[var(--glow-purple)]" />
                Digital Media Capabilities
              </div>

              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.12] tracking-tight text-white">
                Data Intelligence Services That Turn Business Data into Smarter Marketing and Growth Strategies
              </h1>

              <p className="mt-4 text-base font-mono text-white/80 uppercase tracking-widest">
                Data Intelligence, Business Intelligence & Predictive Analytics
              </p>

              <p className="mt-4 text-lg sm:text-xl font-accent italic text-[var(--glow-purple)] font-medium border-l-2 border-[var(--glow-purple)] pl-4 py-1">
                Unlock Actionable Insights Through Advanced Data Intelligence
              </p>

              <div className="mt-6 space-y-4 text-base sm:text-lg text-white/90 font-normal leading-relaxed">
                <p>
                  Modern businesses generate vast amounts of data across websites, ad platforms, CRM systems, social media, mobile apps, and customer support channels. However, data alone has little value unless transformed into actionable strategic direction.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  Data Intelligence combines collection, integration, analytics, real-time visualization, and predictive modeling to deliver a unified single source of truth across your commercial ecosystem.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  At <strong className="text-white">KMWAdvertising LLP</strong>, we help organisations convert complex raw data into measurable business value, empowering executive teams to make faster, more confident growth investments.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <a href="/contact-us" className="btn-hero !py-3.5 !px-7">
                  Request Data Intelligence Proposal
                  <ArrowRight className="h-4 w-4" />
                </a>
                <Link to="/services" className="btn-ghost !py-3.5 !px-7">
                  Explore All Services
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* WHY DATA INTELLIGENCE MATTERS */}
        <section className="py-14 sm:py-20 border-b border-white/10 bg-white/[0.01]">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                Business Intelligence Pillars
              </span>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-white">
                Why Data Intelligence Matters
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-3 leading-relaxed">
                Organizations using data effectively reduce strategic uncertainty, spot market shifts early, and maximize marketing performance.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {whyDataIntelligenceMatters.map((item, idx) => {
                const DIcon = item.icon;
                return (
                  <div
                    key={idx}
                    className="p-6 sm:p-8 rounded-2xl glass-strong border border-white/10 hover:border-white/20 transition duration-300 flex flex-col justify-between group"
                  >
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)]">
                          PILLAR 0{idx + 1}
                        </span>
                        <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white">
                          <DIcon className="h-5 w-5 text-[var(--glow-purple)]" />
                        </div>
                      </div>
                      <h3 className="font-display text-xl font-bold text-white mb-2">
                        {item.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* SERVICES SUITE & PREDICTIVE MODELING */}
        <section className="py-16 md:py-24 border-b border-white/10 relative">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                Unified Data Architecture
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Our Data Intelligence Services
              </h2>
            </div>

            {/* 6 SERVICES CARDS */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
              {dataIntelligenceServices.map((svc, idx) => {
                const SIcon = svc.icon;
                return (
                  <div key={idx} className="p-6 sm:p-8 rounded-2xl glass-strong border border-white/10 hover:border-white/20 transition duration-300 space-y-4">
                    <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-[var(--glow-purple)]">
                      <SIcon className="h-5 w-5" />
                    </div>
                    <h4 className="font-display text-xl font-bold text-white">{svc.title}</h4>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{svc.desc}</p>
                  </div>
                );
              })}
            </div>

            {/* PREDICTIVE CAPABILITIES & TECH STACK */}
            <div className="grid lg:grid-cols-12 gap-8 items-start">
              {/* Predictive Modeling */}
              <div className="lg:col-span-6 p-8 rounded-3xl glass-strong border border-white/10 space-y-4">
                <h4 className="font-display text-xl font-bold text-white flex items-center gap-2">
                  <Cpu className="h-5 w-5 text-[var(--glow-purple)]" />
                  Predictive Modeling Capabilities
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Forecasting trends and anticipating customer behavior using statistical analysis:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
                  {predictiveModelingCapabilities.map((pred, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                      <span>{pred}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Technologies */}
              <div className="lg:col-span-6 p-8 rounded-3xl glass-strong border border-white/10 bg-[#06060c]/90 space-y-4">
                <h4 className="font-display text-xl font-bold text-white flex items-center gap-2">
                  <Database className="h-5 w-5 text-sky-400" />
                  Technologies & Platforms
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  We leverage enterprise analytics engines, cloud data warehouses, and BI visualization suites:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
                  {techPlatformsList.map((plat, i) => (
                    <div key={i} className="p-2.5 rounded-xl bg-white/[0.03] border border-white/5 text-xs text-white/90 font-medium">
                      {plat}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WHY CHOOSE KMWA LLP */}
        <section className="py-16 md:py-24 border-b border-white/10 relative">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                25+ Years Strategic Analytics Heritage
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Why Choose KMWAdvertising LLP?
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {kmwaStrengths.map((str, i) => (
                <div key={i} className="p-4 rounded-xl bg-white/[0.03] border border-white/5 space-y-2">
                  <span className="font-mono text-xs text-[var(--glow-purple)] font-bold">0{i + 1}</span>
                  <p className="text-xs text-white/90 leading-snug font-medium">{str}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CLOSING CTA BANNER */}
        <section className="py-20 md:py-28 relative overflow-hidden">
          <div className="glow-orb left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] bg-[var(--glow-purple)] opacity-20" />

          <div className="container-x relative z-10 text-center max-w-4xl">
            <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-widest text-muted-foreground border border-white/10 mb-6">
              <Database className="h-3.5 w-3.5 text-[var(--glow-purple)]" />
              Data Intelligence Advisory
            </div>

            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight">
              Ready to Turn Business Data into Growth Strategies?
            </h2>

            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Contact our Data Intelligence team today for a tailored Business Intelligence assessment and predictive analytics consultation.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="/contact-us" className="btn-hero w-full sm:w-auto">
                Request Data Intelligence Proposal
                <ArrowRight className="h-4 w-4" />
              </a>
              <Link to="/services" className="btn-ghost w-full sm:w-auto">
                Back to All Services
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
